import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import portalApi, { getPortalErrorMessage } from '@/lib/portalApi';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/components/common/PasswordInput';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Heart, CheckCircle, AlertCircle } from 'lucide-react';

const MODES = {
  invite: { title: 'Create your password', description: 'Set a password to start using the participant portal.', endpoint: '/portal/auth/accept-invite', button: 'Create password' },
  reset: { title: 'Choose a new password', description: 'Enter a new password for your portal account.', endpoint: '/portal/auth/reset-password', button: 'Save new password' },
};

/** Opened from the emailed link: /portal/accept-invite?token=… (mode "invite") or /portal/reset-password?token=… ("reset"). */
export default function PortalSetPasswordPage({ mode = 'invite' }) {
  const m = MODES[mode] || MODES.invite;
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setError('The passwords do not match'); return; }
    setSaving(true);
    try {
      await portalApi.post(m.endpoint, { token, password });
      setDone(true);
    } catch (err) {
      setError(getPortalErrorMessage(err));
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md" data-testid={`portal-set-password-${mode}`}>
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="h-10 w-10 rounded-lg bg-pink-500 flex items-center justify-center"><Heart className="h-5 w-5 text-white" /></div>
          </div>
          <CardTitle className="text-xl">{m.title}</CardTitle>
          <CardDescription>{m.description}</CardDescription>
        </CardHeader>
        <CardContent>
          {!token ? (
            <p className="text-sm text-center text-muted-foreground" data-testid="portal-set-password-no-token">This link is incomplete. Open the link from your email again.</p>
          ) : done ? (
            <div className="text-center space-y-4" data-testid="portal-set-password-done">
              <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto" />
              <p className="text-sm text-muted-foreground">Your password is saved. You can now sign in.</p>
              <Link to="/portal/login"><Button className="w-full bg-pink-500 hover:bg-pink-600">Go to sign in</Button></Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2"><Label htmlFor="pp-new">New password</Label>
                <PasswordInput id="pp-new" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password" data-testid="portal-new-password" /></div>
              <div className="space-y-2"><Label htmlFor="pp-confirm">Confirm password</Label>
                <PasswordInput id="pp-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} required autoComplete="new-password" data-testid="portal-confirm-password" /></div>
              <p className="text-xs text-muted-foreground">At least 8 characters.</p>
              {error && <p className="text-sm text-red-600 flex items-center gap-1.5" data-testid="portal-set-password-error"><AlertCircle className="h-4 w-4" />{error}</p>}
              <Button type="submit" className="w-full bg-pink-500 hover:bg-pink-600" disabled={saving} data-testid="portal-set-password-submit">{saving ? 'Saving…' : m.button}</Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
